import React, {Component, ReactNode} from 'react'
import {Nullable} from '../../app/TypeHelpers'
import {LOGIN_MODE, MESSAGE_TYPE, VOTE_STATUS, VOTING_PREFERENCE_RESPONSE} from '../../CodeMaster'
import ResidentGeneralMeetingRepo, {ResidentProposalResponse} from './ResidentGeneralMeetingRepo'
import {
    ResidentVotingPreferenceProposalResponseInputLocationState
} from './ResidentVotingPreferenceProposalResponseInputPage'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import {Box} from '@mui/material'
import {votingPreferenceStepsForPc, votingPreferenceStepsForSp} from './ResidentVotingPreferenceMethodSelectionPage'
import {PrimaryStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {PlainBackLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import styled from 'styled-components'
import ResidentSectionComponent from '../shared/ResidentSectionComponent'
import ResidentLabeledContentComponent from '../shared/ResidentLabeledContentComponent'
import {ColumnFlexContainer} from '../../styledComponents/FlexComponents'
import {GeneralMeetingProposal} from './model/GeneralMeetingProposal'
import {Colors, FontWeights} from '../../styledComponents/Constants'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {
    ResidentLoginInfoSessionStorageKey
} from '../../__NoAutomatedTests/common/authentication/NatResidentLoginInfoSessionStorageHelper'
import MessageDialogComponent from '../../common/resident/MessageDialogComponent'
import {Messages} from '../../Messages'
import {Location} from 'react-router-dom'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'
import {HttpError} from '../../network/errors/HttpError'

export interface ResidentVotingPreferenceProposalResponseConfirmationLocationState {
    votingPreferenceId: Nullable<number>
    assocId: number
    assocName: string
    votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE
    votingPreferenceVersion: Nullable<number>
    residentProposalResponseList: Nullable<ResidentProposalResponse[]>
    generalMeetingProposalList: GeneralMeetingProposal[]
    beforeNetRespondedAssocIdList: number[]
}

interface Props {
    location: Location<ResidentVotingPreferenceProposalResponseConfirmationLocationState>
    condominiumCd: string
    generalMeetingId: number
    assocId: number
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    showAlert: boolean
    isOpenAdminDialog: boolean
}

class ResidentVotingPreferenceProposalResponseConfirmationPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        if (!this.props.location.state) {
            this.props.navigate('/resident/error')
        }

        if (!this.props.location.state.residentProposalResponseList) {
            return
        }

        const showAlert = this.props.location.state.residentProposalResponseList.some(
            response => response.proposalResponse.value === VOTE_STATUS.ABSTAIN.value
        )

        this.state = {
            showAlert: showAlert,
            isOpenAdminDialog: false,
        }

        this.handleConfirmButtonClick = this.handleConfirmButtonClick.bind(this)
        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
        this.handleAdminDialogOkClick = this.handleAdminDialogOkClick.bind(this)
    }

    isAdminUser(): boolean {
        return SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_MODE) === LOGIN_MODE.ADMIN.value
    }

    async handleConfirmButtonClick(): Promise<void> {
        if (this.isAdminUser()) {
            this.setState({isOpenAdminDialog: true})
            return
        }

        if (!this.props.location.state) {
            return
        }

        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            residentGeneralMeetingRepo,
            navigate,
            eventEmitter,
        } = this.props

        const {
            votingPreferenceId,
            votingPreferenceResponse,
            residentProposalResponseList,
            votingPreferenceVersion,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                // @ts-ignore
                residentGeneralMeetingRepo.saveGeneralMeetingVotingPreference.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    assocId,
                    votingPreferenceId,
                    votingPreferenceResponse,
                    null,
                    residentProposalResponseList,
                    votingPreferenceVersion,
                    true,
                ),
                async (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError instanceof HttpError) {
                        navigate(
                            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                            {
                                state: {
                                    votingPreferenceHttpError: {
                                        status: httpErrorOrNetworkError.status,
                                        responseBody: httpErrorOrNetworkError.responseBody,
                                    },
                                },
                            },
                        )
                    }
                },
            )

            if (!response || response.requestError !== null) {
                return
            }

            navigate(
                `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/complete`,
                {
                    state: {
                        beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
                    },
                },
            )
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleBackButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            navigate
        } = this.props

        const {
            assocId: stateAssocId,
            assocName,
            votingPreferenceId,
            votingPreferenceResponse,
            residentProposalResponseList,
            votingPreferenceVersion,
            generalMeetingProposalList,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        const backState: ResidentVotingPreferenceProposalResponseInputLocationState =
            {
                votingPreferenceId: votingPreferenceId,
                assocId: stateAssocId,
                assocName: assocName,
                votingPreferenceResponse: votingPreferenceResponse,
                residentProposalResponseList: residentProposalResponseList,
                votingPreferenceVersion: votingPreferenceVersion,
                generalMeetingProposalList: generalMeetingProposalList,
                beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
            }

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/input/proposal-response`,
            {
                state: backState,
            },
        )
    }

    handleAdminDialogOkClick(): void {
        this.setState({isOpenAdminDialog: false})
    }

    render(): ReactNode {
        if (!this.props.location.state) {
            return null
        }

        const {
            showAlert,
        } = this.state

        const {
            condominiumCd,
            generalMeetingId,
            assocId,
        } = this.props

        const {
            assocId: stateAssocId,
            assocName,
            votingPreferenceId,
            votingPreferenceResponse,
            residentProposalResponseList,
            votingPreferenceVersion,
            generalMeetingProposalList,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        if (!residentProposalResponseList) {
            throw Error('議案に対する回答が無い')
        }

        const responseByProposalId = new Map(
            (residentProposalResponseList).map(
                response => [response.proposalId, response]
            )
        )

        return <>
            <PlainBackLink
                to={`/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/input/proposal-response`}
                state={{
                    votingPreferenceId: votingPreferenceId,
                    assocId: stateAssocId,
                    assocName: assocName,
                    votingPreferenceResponse: votingPreferenceResponse,
                    residentProposalResponseList: residentProposalResponseList,
                    votingPreferenceVersion: votingPreferenceVersion,
                    generalMeetingProposalList: generalMeetingProposalList,
                    beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
                }}
            >
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <HideInSP>
                <PPStepper steps={votingPreferenceStepsForPc} activeStep={2}/>
            </HideInSP>
            <HideInPC>
                <PPStepper steps={votingPreferenceStepsForSp} activeStep={2} alternativeLabel/>
            </HideInPC>
            <Box height={'30px'}/>
            <StyledPageTitle>内容確認</StyledPageTitle>
            <Box height={'20px'}/>
            <div>
                内容をご確認のうえ、意思表明を確定してください。
            </div>
            <Box height={'20px'}/>
            {
                showAlert && <>
                    <Box fontWeight={FontWeights.Bold} color={Colors.Alert}>
                        ※賛成/反対が未選択の場合は、棄権したものと見なします。
                    </Box>
                    <Box height={'15px'}/>
                </>
            }
            <ContentArea>
                <ResidentSectionComponent
                    content={
                        <ResidentLabeledContentComponent
                            label={'対象管理組合'}
                            labelPosition={'flex-start'}
                            content={<AssocName>{assocName}</AssocName>}
                        />
                    }
                />
                <ResidentSectionComponent
                    content={<ResidentLabeledContentComponent
                        label={'総会参加方法の選択'}
                        labelPosition={'flex-start'}
                        content={<Box fontWeight={FontWeights.Bold}>
                            {votingPreferenceResponse.label}
                        </Box>}
                    />}
                />
                <ResidentSectionComponent
                    content={<ResidentLabeledContentComponent
                        label={'議案'}
                        labelPosition={'flex-start'}
                        content={<>
                            {generalMeetingProposalList.map(proposal => {
                                if (proposal.proposalId === null || !proposal.proposalName) {
                                    return null
                                }

                                const response = responseByProposalId.get(proposal.proposalId)
                                if (!response) {
                                    return null
                                }

                                const isAbstain = response.proposalResponse.value === VOTE_STATUS.ABSTAIN.value
                                return <ProposalResponseRow key={proposal.proposalId} isAbstain={isAbstain}>
                                    第{proposal.proposalNumber}号議案：
                                    <b>{!isAbstain ? response.proposalResponse.label : ''}</b>
                                </ProposalResponseRow>
                            })}
                        </>}
                    />}
                />
            </ContentArea>
            <Box height={'20px'}/>
            <PrimaryStyledButton onClick={this.handleConfirmButtonClick}>
                確定する
            </PrimaryStyledButton>
            <Box height={'30px'}/>
            <StyledSecondaryButton onClick={this.handleBackButtonClick}>
                戻る
            </StyledSecondaryButton>
            <Box height={'15px'}/>
            <MessageDialogComponent
                open={this.state.isOpenAdminDialog}
                params={{
                    title: '確認',
                    errorLevel: MESSAGE_TYPE.INFORMATION,
                    message: Messages.ISYCR0001.toString(),
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleAdminDialogOkClick,
                        },
                    ],
                }}
            />
        </>
    }
}

export default createResidentWithErrorHandlingPage(ResidentVotingPreferenceProposalResponseConfirmationPage)

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const ContentArea = styled(ColumnFlexContainer)`
  row-gap: 5px;
`

const AssocName = styled.div`
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const ProposalResponseRow = styled.div<{ isAbstain: boolean }>`
  color: ${props => props.isAbstain ? Colors.Alert : 'inherit'};
`
